import * as React from 'react'

import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { SitePermitStatus, WorkflowStepType } from '~/client/graph'
import { getFormStatusDisplayName } from '~/client/src/shared/localization/enumDisplayTexts'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import {
  getForwardActionLabel,
  getForwardActionTargetStatus,
} from '~/client/src/shared/utils/permitStepActionHelpers'

import PermitApprovalStore from '../../../PermitApproval.store'
import SitePermitCreationFormStore from '../../../WorkflowCreationForm.store'

// localization: translated

interface IProps {
  actionBarButtonsRenderer: (
    btnText: string,
    isPermitValid: boolean,
    status: SitePermitStatus,
    isDeny?: boolean,
    customBtnHandler?: () => void,
  ) => JSX.Element

  store: SitePermitCreationFormStore
  permitApprovalStore: PermitApprovalStore
}

interface IFormButtonConfig {
  caption: string
  status: SitePermitStatus
  isFailedStatus?: boolean
  isValidationIgnored?: boolean
  customBtnHandler?(): void
}

@observer
export default class CommonPermitButtons extends React.Component<IProps> {
  public render() {
    const { actionBarButtonsRenderer, store } = this.props
    const { isPermitValid, isStepAdvancePending, existingPermit, template } =
      store
    const { currentWorkflowStepId, isTerminal, isDeniedOrFailed, status } =
      existingPermit

    if (isTerminal) {
      return (
        <div className="tw:flex tw:justify-center tw:my-2.5">
          <span
            className={classList({
              'tw:text-center tw:text-body-md tw:font-semibold tw:tracking-[0.015em] tw:text-muted-foreground': true,
              'tw:text-destructive': isDeniedOrFailed,
            })}
          >
            {getFormStatusDisplayName(status)}
          </span>
        </div>
      )
    }

    const currentStepType = template.stepsWithoutRecurring.find(
      s => s.id === currentWorkflowStepId,
    )?.type
    const buttonConfig = this.buttonsConfigByStepsMap[currentStepType]

    if (!buttonConfig?.length) return null

    return (
      <div className="tw:flex tw:justify-center tw:gap-2">
        {buttonConfig.map((conf, idx) => (
          <React.Fragment key={idx}>
            {actionBarButtonsRenderer(
              conf.caption,
              !isStepAdvancePending &&
                (conf.isValidationIgnored || isPermitValid),
              conf.status,
              conf.isFailedStatus,
              conf.customBtnHandler,
            )}
          </React.Fragment>
        ))}
      </div>
    )
  }

  private get buttonsConfigByStepsMap(): {
    [K in WorkflowStepType]: IFormButtonConfig[]
  } {
    const { permitApprovalStore, store } = this.props
    const isLastStep = store.isLastStep

    const makeForwardButton = (
      stepType: WorkflowStepType,
    ): IFormButtonConfig | null => {
      const caption = getForwardActionLabel(stepType)
      const targetStatus = getForwardActionTargetStatus(stepType, isLastStep)
      if (!caption || !targetStatus) return null

      return {
        caption,
        status: null,
        customBtnHandler: () =>
          permitApprovalStore.advancePermitWithApproval(targetStatus),
      }
    }

    return {
      [WorkflowStepType.Review]: [makeForwardButton(WorkflowStepType.Review)],
      [WorkflowStepType.Approval]: [
        {
          caption: Localization.translator.deny_verb,
          status: SitePermitStatus.Denied,
          isFailedStatus: true,
          isValidationIgnored: true,
        },
        {
          caption: getForwardActionLabel(WorkflowStepType.Approval),
          status: null,
          customBtnHandler: permitApprovalStore.approvePermit,
        },
      ],
      [WorkflowStepType.Start]: [makeForwardButton(WorkflowStepType.Start)],
      [WorkflowStepType.OnSite]: [makeForwardButton(WorkflowStepType.OnSite)],
      [WorkflowStepType.ReadyToInspect]: [
        makeForwardButton(WorkflowStepType.ReadyToInspect),
      ],
      [WorkflowStepType.Inspection]: [
        {
          caption: Localization.translator.fail,
          status: SitePermitStatus.Failed,
          isFailedStatus: true,
        },
        makeForwardButton(WorkflowStepType.Inspection),
      ],
      [WorkflowStepType.Finish]: [
        {
          caption: getForwardActionLabel(WorkflowStepType.Finish),
          status: SitePermitStatus.Done,
        },
      ],
      [WorkflowStepType.Request]: [],
      [WorkflowStepType.Submission]: [],
      [WorkflowStepType.RecurringInspection]: [],
    }
  }
}
